import { createSlice, PayloadAction } from '@reduxjs/toolkit'

export type InProgressUtilityProgramUnenrollments =
  InProgressUtilityProgramUnenrollment[]

export type InProgressUnenrollmentDeviceType =
  | 'vehicle'
  | 'charger'
  | 'thermostat'
  | 'energySystem'
  | 'profile'

export type InProgressUtilityProgramUnenrollment = {
  utilityProgramId: number
  deviceSelection: {
    vehicleIds: number[]
    chargerIds: number[]
    thermostatIds: number[]
    energySystemIds: string[]
  }
  unenrollmentReasons?: {
    deviceType: InProgressUnenrollmentDeviceType
    reason: string
  }[]
  feedback: string
}

type SetDeviceSelectionPayload = {
  utilityProgramId: number
  deviceSelection: {
    vehicleIds: number[]
    chargerIds: number[]
    thermostatIds: number[]
    energySystemIds: string[]
  }
}

export type SetUnenrollmentReasonPayload = {
  utilityProgramId: number
  reasons: NonNullable<
    InProgressUtilityProgramUnenrollment['unenrollmentReasons']
  >
}

type SetFeedbackPayload = {
  utilityProgramId: number
  feedback: string
}

const inProgressUtilityProgramUnenrollmentsSlice = createSlice({
  name: 'inProgressUtilityProgramUnenrollments',
  initialState: [] as InProgressUtilityProgramUnenrollments,
  reducers: {
    startUnenrollment: (
      state: InProgressUtilityProgramUnenrollments,
      action: PayloadAction<number>,
    ) => {
      if (!state.find((p) => p.utilityProgramId === action.payload)) {
        return [
          ...state,
          {
            utilityProgramId: action.payload,
            deviceSelection: {
              vehicleIds: [],
              chargerIds: [],
              thermostatIds: [],
              energySystemIds: [],
            },
            unenrollmentReasons: undefined,
            feedback: '',
          },
        ]
      }
      return state.map((p) =>
        p.utilityProgramId === action.payload
          ? {
              ...p,
              deviceSelection: {
                vehicleIds: [],
                chargerIds: [],
                thermostatIds: [],
                energySystemIds: [],
              },
              unenrollmentReasons: undefined,
              feedback: '',
            }
          : p,
      )
    },
    setDeviceSelection: (
      state: InProgressUtilityProgramUnenrollments,
      action: PayloadAction<SetDeviceSelectionPayload>,
    ) => {
      if (
        !state.find(
          (p) => p.utilityProgramId === action.payload.utilityProgramId,
        )
      ) {
        return [
          ...state,
          {
            utilityProgramId: action.payload.utilityProgramId,
            deviceSelection: action.payload.deviceSelection,
            unenrollmentReasons: undefined,
            feedback: '',
          },
        ]
      }
      return state.map((p) =>
        p.utilityProgramId === action.payload.utilityProgramId
          ? { ...p, deviceSelection: action.payload.deviceSelection }
          : p,
      )
    },
    setUnenrollmentReason: (
      state: InProgressUtilityProgramUnenrollments,
      action: PayloadAction<SetUnenrollmentReasonPayload>,
    ) => {
      // if there are no existing in progress unenrollments, create a new one
      if (
        !state.find(
          (p) => p.utilityProgramId === action.payload.utilityProgramId,
        )
      ) {
        return [
          ...state,
          {
            utilityProgramId: action.payload.utilityProgramId,
            deviceSelection: {
              vehicleIds: [],
              chargerIds: [],
              thermostatIds: [],
              energySystemIds: [],
            },
            unenrollmentReasons: action.payload.reasons,
            feedback: '',
          },
        ]
      }
      // there is an existin in progress unenrollment, update the reason
      return state.map((p) =>
        p.utilityProgramId === action.payload.utilityProgramId
          ? {
              ...p,
              unenrollmentReasons: action.payload.reasons,
            }
          : p,
      )
    },
    setFeedback: (
      state: InProgressUtilityProgramUnenrollments,
      action: PayloadAction<SetFeedbackPayload>,
    ) => {
      if (
        !state.find(
          (p) => p.utilityProgramId === action.payload.utilityProgramId,
        )
      ) {
        return [
          ...state,
          {
            utilityProgramId: action.payload.utilityProgramId,
            deviceSelection: {
              vehicleIds: [],
              chargerIds: [],
              thermostatIds: [],
              energySystemIds: [],
            },
            unenrollmentReasons: undefined,
            feedback: action.payload.feedback,
          },
        ]
      }
      return state.map((p) =>
        p.utilityProgramId === action.payload.utilityProgramId
          ? { ...p, feedback: action.payload.feedback }
          : p,
      )
    },
    completeUnenrollment: (
      state: InProgressUtilityProgramUnenrollments,
      action: PayloadAction<number>,
    ) => {
      return state.filter((p) => p.utilityProgramId !== action.payload)
    },
  },
})

export const {
  startUnenrollment,
  setDeviceSelection,
  setUnenrollmentReason,
  setFeedback,
  completeUnenrollment,
} = inProgressUtilityProgramUnenrollmentsSlice.actions

export const selectInProgressUnenrollment =
  (utilityProgramId: number) =>
  (state: {
    inProgressUtilityProgramUnenrollments: InProgressUtilityProgramUnenrollments
  }) =>
    state.inProgressUtilityProgramUnenrollments.find(
      (p) => p.utilityProgramId === utilityProgramId,
    )

export const selectUnenrollmentProgress =
  (utilityProgramId: number) =>
  (state: {
    inProgressUtilityProgramUnenrollments: InProgressUtilityProgramUnenrollments
  }) => {
    const unenrollment = state.inProgressUtilityProgramUnenrollments.find(
      (p) => p.utilityProgramId === utilityProgramId,
    )
    if (!unenrollment) {
      return { totalSteps: 3, completedSteps: 5 }
    }

    const totalSteps = 3
    let completedSteps = 0

    const hasDeviceSelection =
      unenrollment.deviceSelection.vehicleIds.length > 0 ||
      unenrollment.deviceSelection.chargerIds.length > 0 ||
      unenrollment.deviceSelection.thermostatIds.length > 0

    if (hasDeviceSelection) {
      completedSteps += 1
    }

    if (unenrollment.unenrollmentReasons) {
      completedSteps += 1
    }

    if (unenrollment.feedback !== '') {
      completedSteps += 1
    }

    return { totalSteps, completedSteps }
  }

export default inProgressUtilityProgramUnenrollmentsSlice.reducer
